import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {
    CertificateApi,
    CertificateStore,
    KeyExportFormat,
    ObjectEncoding,
    X509CertificateDetails
} from "components/src/api/admin/generated";
import {Download} from "components/src/common/Download";
import {DialogCloseCause} from "components/src/common/Enums";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import {useNavigate} from "react-router-dom";

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface CertificateExportDialogProps {
    certificateStore : CertificateStore
    certificates : X509CertificateDetails[]
    onClose : OnClose
}

/*
 * REST api for accessing the back-end
 */
const certificateApi = new CertificateApi(ApiConfig.config());

export const CertificateExportDialog = ({certificateStore, certificates, onClose} : CertificateExportDialogProps ) =>
{
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated
     */
    const navigate = useNavigate();
    /**
     * True if private key export is selected
     */
    const [exportPrivateKeys, setExportPrivateKeys] = useState<boolean>(false);
    /**
     * The export password (only used when exporting private keys)
     */
    const [password, setPassword] = useState<string>();
    /**
     * The encoding (PEM vs DER) of the export
     */
    const [encoding, setEncoding] = useState<ObjectEncoding>(ObjectEncoding.Pem);
    /**
     * If true, a Download component will be loaded which will download the binary property
     */
    const [download, setDownload] = useState(false);
    /**
     * The blob containing the binary file to download
     */
    const [blob, setBlob] = useState<Blob>();
    /**
     * The error message if some general error occurred
     */
    const [errorMessage, setErrorMessage] = useState<string>();

    /**
     * Return true if at least one private key is available.
     */
    const isPrivateKeyWithinSelection = () => {
        return certificates.some(cert => cert.keyAlias !== undefined && cert.keyAlias?.length > 0);
    }

    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    /**
     * The filename for the certificate file depends on the encoding and on the number of exported certificates
     */
    function getFileExtension()
    {
        if (encoding === ObjectEncoding.Pem) {
            return "pem";
        }

        if (exportPrivateKeys) {
            return "p12"
        }

        return certificates.length > 1 ? "p7b" : "cer";
    }

    /**
     * The content-type for the certificate file depends on the encoding and on the number of exported certificates
     */
    function getContentType()
    {
        if (encoding === ObjectEncoding.Pem) {
            return "application/x-pem-file";
        }

        if (exportPrivateKeys) {
            return "application/x-pkcs12 "
        }

        return certificates.length > 1 ? "application/x-pkcs7-certificates" : "application/pkix-cert";
    }

    const exportCertificates = () =>
    {
        const thumbprints = certificates.map(cert => cert.thumbprint);

        // the REST call depends on whether private keys are exported or not
        const exportCertificatesFunction = exportPrivateKeys ?
             async () => {
                return await certificateApi.certificateControllerExportPrivateKeys(certificateStore,
                    encoding === ObjectEncoding.Pem ? KeyExportFormat.Pem : KeyExportFormat.Pkcs12,
                    {thumbprints : thumbprints, keyStorePassword : password??""},
                    true, {responseType : 'blob'});
            } :
            async () => {
                return await certificateApi.certificateControllerExportCertificates(certificateStore, encoding,
                    thumbprints, {responseType : 'blob'})
            }

        // NOTE: responseType must be set to 'blob' because the response is binary
        exportCertificatesFunction().then((response) =>
        {
            setBlob(new Blob([response.data], {type: getContentType()}));

            // enable the Download component
            setDownload(true);
        })
        .catch((err: Error | AxiosError) => {
            logger.error("Error exporting certificates", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
    }

    /**
     * Is called when the download started. This should reset download to make sure the Download component is no
     * longer added.
     */
    const onDownload = () =>
    {
        // reset password
        setPassword(undefined);
        // disable the Download component
        setDownload(false);
        onClose(DialogCloseCause.Succes);
    }

    // called when the dialog is closed without clicking export
    const onHideDialog = () => {
        // reset password
        setPassword(undefined);
        onClose(DialogCloseCause.Cancel);
    }

    return (
        <Modal show={true} onHide={() => onHideDialog()} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("certificates.export-certificates")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>

                <Form.Group className="mb-3">
                    <Form.Label>{t("certificates.encoding")}</Form.Label>
                    <Form.Check value="PEM"
                                id="pem-encoding"
                                type="radio"
                                name="encoding"
                                checked={encoding === ObjectEncoding.Pem}
                                onChange={(e) => setEncoding(Utils.stringToEnum(ObjectEncoding, e.target.value as ObjectEncoding))}
                                label={t("certificates.pem")}

                    />
                    <Form.Check value="DER"
                                id="der-encoding"
                                type="radio"
                                name="encoding"
                                checked={encoding === ObjectEncoding.Der}
                                onChange={(e) => setEncoding(Utils.stringToEnum(ObjectEncoding, e.target.value as ObjectEncoding))}
                                label={t("certificates.der")}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Check
                        type="switch"
                        id="export-private-keys"
                        label={t("certificates.export-private-keys")}
                        disabled={!isPrivateKeyWithinSelection()}
                        onChange={(e) => setExportPrivateKeys(e.target.checked)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>{t("certificates.private-key-password")}</Form.Label>
                    <Form.Control type="password"
                                  placeholder={t("certificates.private-key-password")}
                                  disabled={!exportPrivateKeys}
                                  onChange={e => setPassword(e.target.value)}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="success"
                        onClick={exportCertificates}
                        disabled={exportPrivateKeys && (password??"").length === 0}>
                    Export
                </Button>
            </Modal.Footer>

            {download && blob !== undefined && (
                <Download blob={blob}
                          filename={`certificates.${getFileExtension()}`}
                          onDownload={onDownload} />
            )}
        </Modal>
    )
}
